import { z } from "zod";

/**
 * Allowlist registry — add new marks here (e.g. "highlight") then teach
 * parser filter, React/Remotion renderers, and narration mapper.
 */
export const PRESENTATION_RICH_TEXT_MARKS = [
	"bold",
	"italic",
	"underline",
] as const;

export type PresentationRichTextMark =
	(typeof PRESENTATION_RICH_TEXT_MARKS)[number];

export const PRESENTATION_RICH_TEXT_NODES = [
	"doc",
	"paragraph",
	"text",
	"hardBreak",
	"bulletList",
	"orderedList",
	"listItem",
] as const;

export type PresentationRichTextNodeType =
	(typeof PRESENTATION_RICH_TEXT_NODES)[number];

export const PRESENTATION_RICH_TEXT_MARK_SET = new Set<string>(
	PRESENTATION_RICH_TEXT_MARKS,
);

export const PRESENTATION_RICH_TEXT_NODE_SET = new Set<string>(
	PRESENTATION_RICH_TEXT_NODES,
);

export const presentationRichTextMarkSchema = z.enum(
	PRESENTATION_RICH_TEXT_MARKS,
);

export interface PresentationRichTextTextNode {
	type: "text";
	text: string;
	marks?: PresentationRichTextMark[];
}

export interface PresentationRichTextHardBreak {
	type: "hardBreak";
}

export type PresentationRichTextInline =
	| PresentationRichTextTextNode
	| PresentationRichTextHardBreak;

export interface PresentationRichTextParagraph {
	type: "paragraph";
	content?: PresentationRichTextInline[];
}

export interface PresentationRichTextListItem {
	type: "listItem";
	content?: Array<
		| PresentationRichTextParagraph
		| PresentationRichTextBulletList
		| PresentationRichTextOrderedList
	>;
}

export interface PresentationRichTextBulletList {
	type: "bulletList";
	content?: PresentationRichTextListItem[];
}

export interface PresentationRichTextOrderedList {
	type: "orderedList";
	content?: PresentationRichTextListItem[];
}

export type PresentationRichTextBlock =
	| PresentationRichTextParagraph
	| PresentationRichTextBulletList
	| PresentationRichTextOrderedList;

/** Any non-inline node: a top-level block or a list item. */
export type PresentationRichTextNode =
	| PresentationRichTextBlock
	| PresentationRichTextListItem;

export interface PresentationRichTextV1 {
	type: "doc";
	version: 1;
	content?: PresentationRichTextBlock[];
}

const inlineSchema = z.union([
	z.object({
		type: z.literal("text"),
		text: z.string(),
		marks: z.array(presentationRichTextMarkSchema).optional(),
	}),
	z.object({ type: z.literal("hardBreak") }),
]);

const paragraphSchema = z.object({
	type: z.literal("paragraph"),
	content: z.array(inlineSchema).optional(),
});

type ListItemShape = {
	type: "listItem";
	content?: Array<
		| z.infer<typeof paragraphSchema>
		| { type: "bulletList"; content?: ListItemShape[] }
		| { type: "orderedList"; content?: ListItemShape[] }
	>;
};

const listItemSchema: z.ZodType<ListItemShape> = z.lazy(() =>
	z.object({
		type: z.literal("listItem"),
		content: z
			.array(
				z.union([
					paragraphSchema,
					z.object({
						type: z.literal("bulletList"),
						content: z.array(listItemSchema).optional(),
					}),
					z.object({
						type: z.literal("orderedList"),
						content: z.array(listItemSchema).optional(),
					}),
				]),
			)
			.optional(),
	}),
);

const blockSchema = z.union([
	paragraphSchema,
	z.object({
		type: z.literal("bulletList"),
		content: z.array(listItemSchema).optional(),
	}),
	z.object({
		type: z.literal("orderedList"),
		content: z.array(listItemSchema).optional(),
	}),
]);

export const presentationRichTextV1Schema: z.ZodType<PresentationRichTextV1> =
	z.object({
		type: z.literal("doc"),
		version: z.literal(1),
		content: z.array(blockSchema).optional(),
	});

/** Narration emphasis tag SoT for bold (ElevenLabs catalog). */
export const PRESENTATION_BOLD_AUDIO_TAG = "[emphasized]" as const;

/** Softer catalog tag for italic / underline. */
export const PRESENTATION_SOFT_EMPHASIS_AUDIO_TAG = "[soft emphasis]" as const;
